import { 
  BackgroundAnalysisResponse, 
  BackgroundAnalysisStatus,
  BackgroundAnalysisOptions,
  Analysis,
  AnalysisFormData
} from '../types';
import { getCsrfToken, resetCsrfToken, getCurrentCsrfToken } from './csrfService';

// URL share-server API
const SHARE_SERVER_URL = import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002';

// Функция для форматирования времени
function formatTime(ms: number): string {
  if (ms < 1000) return `${ms}мс`;
  if (ms < 60000) return `${Math.floor(ms / 1000)}с`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}м ${seconds}с`;
}

// Функция для расчета прогресса
function calculateProgress(timeSinceStart: number, averageAnalysisTime: number): number | null {
  if (!averageAnalysisTime || averageAnalysisTime <= 0) return null;
  const progress = Math.min(100, (timeSinceStart / averageAnalysisTime) * 100);
  return Math.round(progress);
}

// Функция для создания прогресс-бара
function createProgressBar(progress: number): string {
  const barLength = 20;
  const filledLength = Math.floor((progress / 100) * barLength);
  const emptyLength = barLength - filledLength;
  return `[${'█'.repeat(filledLength)}${'░'.repeat(emptyLength)}] ${progress}%`;
}

// Функция для подготовки описания с экспертами и личностями
function prepareDescription(data: AnalysisFormData): string {
  let description = data.description;
  
      console.log('🔍 backgroundAnalysisService - Исходные данные:', { 
      description: data.description, 
      expert: data.expert, 
      person: data.person,
      landing: data.landing,
      question: data.question,
      noQuestion: data.noQuestion
    });
    console.log('🔍 backgroundAnalysisService - Проверка полей:', {
      'data.question': data.question,
      'typeof data.question': typeof data.question,
      'Boolean(data.question)': Boolean(data.question),
      'data.noQuestion': data.noQuestion,
      'typeof data.noQuestion': typeof data.noQuestion,
      'Boolean(data.noQuestion)': Boolean(data.noQuestion)
    });
  
  // Если пользователь выбрал эксперта, добавляем его как переменную
  if (data.expert && data.expert.trim()) {
    description = `${description}|${data.expert.trim()}|`;
    console.log('🔍 backgroundAnalysisService - Добавлен эксперт как переменная:', description);
  }
  
  // Если пользователь выбрал личность, добавляем её как переменную
  if (data.person && data.person.trim()) {
    description = `${description}|${data.person.trim()}|`;
    console.log('🔍 backgroundAnalysisService - Добавлена личность как переменная:', description);
  }
  
  // Добавляем пакет в зависимости от выбранных опций
  if (data.person && data.person.trim() && data.expert && data.expert.trim()) {
    // Если выбраны и эксперт, и личность, добавляем пакет person (приоритет личности)
    description = `#person#${description}`;
    console.log('🔍 backgroundAnalysisService - Добавлен пакет person для выбранной личности:', description);
  } else if (data.person && data.person.trim()) {
    // Если выбрана личность, добавляем только пакет person
    description = `#person#${description}`;
    console.log('🔍 backgroundAnalysisService - Добавлен пакет person для выбранной личности:', description);
  } else if (data.expert && data.expert.trim()) {
    // Если выбран эксперт, добавляем только пакет expert
    description = `#expert#${description}`;
    console.log('🔍 backgroundAnalysisService - Добавлен пакет expert для выбранного эксперта:', description);
  } else if (data.landing) {
    // Если не выбраны эксперт/личность, добавляем пакет в зависимости от лендинга
    switch (data.landing) {
      case 'financial':
        description = `#financial#${description}`;
        console.log('🔍 backgroundAnalysisService - Добавлен пакет financial для financial лендинга:', description);
        break;
      case 'career':
        description = `#career#${description}`;
        console.log('🔍 backgroundAnalysisService - Добавлен пакет career для career лендинга:', description);
        break;
      case 'startup':
        // Для startup лендинга используем пакет expert и автоматически выбираем бизнес-консультанта
        description = `#expert#${description}`;
        // Автоматически добавляем бизнес-консультанта как эксперта для стартапов
        if (!data.expert || !data.expert.trim()) {
          description = `${description}|бизнес-консультант|`;
          console.log('🔍 backgroundAnalysisService - Автоматически добавлен бизнес-консультант для startup лендинга:', description);
        }
        console.log('🔍 backgroundAnalysisService - Добавлен пакет expert для startup лендинга:', description);
        break;
      // Для 'personal' и 'main' не добавляем пакет автоматически
    }
  }
  
  console.log('🔍 backgroundAnalysisService - Финальное описание для отправки:', description);
  return description;
}

/**
 * Создает фоновую задачу анализа
 */
export async function createBackgroundAnalysis(
  data: AnalysisFormData
): Promise<BackgroundAnalysisResponse> {
  try {
    // Проверяем, есть ли уже токен в кэше
    let csrfToken = getCurrentCsrfToken();
    if (!csrfToken) {
      console.log('CSRF: Токен не найден, запрашиваем новый');
      csrfToken = await getCsrfToken();
    } else {
      console.log('CSRF: Используем существующий токен из кэша');
    }

    // Подготавливаем описание с экспертами и личностями
    const preparedDescription = prepareDescription(data);

    // Подготавливаем данные для отправки
    const requestData: any = {
      description: preparedDescription,
      url: data.url
    };
    
    // Добавляем question и noQuestion если они есть в data
    if ('question' in data && data.question !== undefined) {
      requestData.question = data.question;
    }
    if ('noQuestion' in data && data.noQuestion !== undefined) {
      requestData.noQuestion = data.noQuestion;
    }
    
    console.log('🔍 backgroundAnalysisService - Отправляем на сервер:', requestData);

    const response = await fetch(`${SHARE_SERVER_URL}/api/background/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      credentials: 'include',
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Если ошибка CSRF, сбрасываем токен и пробуем еще раз
      if (errorData.code === 'CSRF_ERROR') {
        resetCsrfToken();
        throw new Error('Ошибка CSRF токена. Попробуйте еще раз.');
      }
      
      throw new Error(errorData.error || `HTTP ошибка: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    // Если это ошибка получения CSRF токена, пробуем без него
    if (error instanceof Error && error.message.includes('CSRF')) {
      console.warn('CSRF токен недоступен, пробуем без защиты');
      
      // Подготавливаем описание с экспертами и личностями
      const preparedDescription = prepareDescription(data);
      
      const response = await fetch(`${SHARE_SERVER_URL}/api/background/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description: preparedDescription,
          url: data.url,
          ...(data.question !== undefined && { question: data.question }),
          ...(data.noQuestion !== undefined && { noQuestion: data.noQuestion })
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ошибка: ${response.status}`);
      }

      const result = await response.json();
      return result;
    }
    
    throw error;
  }
}

/**
 * Получает статус фоновой задачи анализа
 */
export async function getBackgroundAnalysisStatus(taskId: string): Promise<BackgroundAnalysisStatus> {
  const response = await fetch(`${SHARE_SERVER_URL}/api/background/status/${taskId}`);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ошибка: ${response.status}`);
  }

  return await response.json();
}

/**
 * Получает результат анализа по external_id
 */
export async function getAnalysisByExternalId(externalId: string): Promise<Analysis> {
  const response = await fetch(`${SHARE_SERVER_URL}/api/analysis/${externalId}`);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ошибка: ${response.status}`);
  }

  const data = await response.json();
  
  // Преобразуем данные из API в формат приложения
  const analysis: Analysis = {
    id: data.id,
    external_id: data.external_id,
    title: data.title,
    description: data.description,
    url: data.url,
    items: data.items.map((item: any) => ({
      id: item.id,
      text: item.text,
      rating: item.rating,
      category: item.category,
      isModified: false, // По умолчанию не изменен
      isExpanded: false, // По умолчанию не расширен
    })),
    createdAt: new Date(data.created_at),
    recommendation: data.recommendation,
    summary: data.summary,
    improvementSuggestions: data.improvementSuggestions || data.improvement_suggestions,
    improvedRequest: data.improvedRequest || data.improved_request,
    actionPlan: data.actionPlan || data.action_plan,
    expert: data.expert,
    person: data.person,
    question: data.question,
    noQuestion: data.no_question,
    isModified: false, // По умолчанию не изменен
    userRating: data.userRating || null,
    category: data.category || null, // Категория анализа
    analysisMode: data.analysisMode || null,
    argumentSelectionLogic: data.argumentSelectionLogic || null,
    plusListTitle: data.plusListTitle || null,
    minusListTitle: data.minusListTitle || null,
    seo: data.seo || null, // SEO-метаданные
  };
  
  return analysis;
}

/**
 * Выполняет фоновый анализ с отслеживанием прогресса
 */
export async function performBackgroundAnalysis(
  data: AnalysisFormData,
  options: BackgroundAnalysisOptions = {},
  abortSignal?: AbortSignal
): Promise<Analysis> {
  const {
    pollInterval = 5000, // 5 секунд по умолчанию
    onStatusUpdate,
    onProgress,
    onCancelled
  } = options;

  // Шаг 1: Создаем фоновую задачу
  const backgroundResponse = await createBackgroundAnalysis(data);
  

  console.log('✅ Фоновая задача создана:', {
    taskId: backgroundResponse.taskId,
    status: backgroundResponse.status,
    analysisNumber: backgroundResponse.analysisNumber,
    queuePosition: backgroundResponse.queuePosition,
    totalInQueue: backgroundResponse.totalInQueue
  });

  // Шаг 2: Отслеживаем статус задачи
  let attempts = 0;
  const maxAttempts = 1000; // Максимальное количество попыток (защита от бесконечного цикла)

  while (attempts < maxAttempts) {
    attempts++;
    
    // Проверяем, не был ли анализ отменен
    if (abortSignal?.aborted) {
      console.log('❌ Анализ отменен пользователем (начало цикла)');
      console.log('❌ AbortSignal.aborted:', abortSignal.aborted);
      console.log('❌ TaskId:', backgroundResponse.taskId);
      if (onCancelled) {
        onCancelled();
      }
      throw new Error('Анализ отменен пользователем');
    }
    
    try {
      const status = await getBackgroundAnalysisStatus(backgroundResponse.taskId);

      // Проверяем отмену после получения статуса
      if (abortSignal?.aborted) {
        console.log('❌ Анализ отменен пользователем после получения статуса');
        console.log('❌ AbortSignal.aborted:', abortSignal.aborted);
        console.log('❌ TaskId:', backgroundResponse.taskId);
        if (onCancelled) {
          onCancelled();
        }
        throw new Error('Анализ отменен пользователем');
      }

      // Вызываем callback для обновления статуса
      if (onStatusUpdate) {
        onStatusUpdate(status);
      }

      // Вызываем callback для обновления прогресса
      if (onProgress && status.timeSinceStart && status.averageAnalysisTime) {
        const progress = calculateProgress(status.timeSinceStart, status.averageAnalysisTime);
        if (progress !== null) {
          onProgress(progress);
        }
      }

      console.log(`📊 Статус задачи (попытка ${attempts}):`, {
        status: status.status,
        analysisNumber: status.analysisNumber,
        queuePosition: status.queuePosition,
        estimatedTimeRemaining: status.estimatedTimeRemaining ? formatTime(status.estimatedTimeRemaining) : 'недоступно',
        averageAnalysisTime: status.averageAnalysisTime ? formatTime(status.averageAnalysisTime) : 'недоступно',
        timeSinceStart: status.timeSinceStart ? formatTime(status.timeSinceStart) : 'недоступно'
      });

      // Если задача завершена, получаем результат
      if (status.status === 'completed' && status.data?.external_id) {
        console.log('✅ Анализ завершен, получаем результат...');
        const analysis = await getAnalysisByExternalId(status.data.external_id);
        
        // Проверяем отмену после получения результата
        if (abortSignal?.aborted) {
          console.log('❌ Анализ отменен пользователем после получения результата');
          console.log('❌ AbortSignal.aborted:', abortSignal.aborted);
          console.log('❌ TaskId:', backgroundResponse.taskId);
          if (onCancelled) {
            onCancelled();
          }
          throw new Error('Анализ отменен пользователем');
        }
        
        console.log('✅ Результат анализа получен:', {
          id: analysis.id,
          external_id: analysis.external_id,
          title: analysis.title,
          itemsCount: analysis.items.length
        });
        return analysis;
      }

      // Если задача завершилась с ошибкой
      if (status.status === 'failed') {
        const errorMessage = status.data?.error || 'Анализ завершился с ошибкой';
        
        // Если это validation_error с примерами, возвращаем специальный результат
        if (status.data?.type === 'validation_error' && status.data?.examples) {
          console.log('❌ Validation error с примерами, возвращаем специальный результат');
          // Возвращаем объект, который будет обработан как validation_error
          return {
            __validationError: true,
            error: errorMessage,
            examples: status.data.examples,
            taskId: status.taskId
          } as any;
        }
        
        throw new Error(errorMessage);
      }

      // Ждем перед следующей проверкой
      if (abortSignal) {
        await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(resolve, pollInterval);
          abortSignal.addEventListener('abort', () => {
            clearTimeout(timeoutId);
            reject(new Error('Анализ отменен пользователем'));
          });
        });
      } else {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
      
      // Проверяем отмену после ожидания
      if (abortSignal?.aborted) {
        console.log('❌ Анализ отменен пользователем после ожидания');
        console.log('❌ AbortSignal.aborted:', abortSignal.aborted);
        console.log('❌ TaskId:', backgroundResponse.taskId);
        if (onCancelled) {
          onCancelled();
        }
        throw new Error('Анализ отменен пользователем');
      }

    } catch (error) {
      console.error(`❌ Ошибка при проверке статуса (попытка ${attempts}):`, error);
      
      // Проверяем отмену в блоке catch
      if (abortSignal?.aborted) {
        console.log('❌ Анализ отменен пользователем в блоке catch');
        console.log('❌ AbortSignal.aborted:', abortSignal.aborted);
        console.log('❌ TaskId:', backgroundResponse.taskId);
        if (onCancelled) {
          onCancelled();
        }
        throw new Error('Анализ отменен пользователем');
      }
      
      // Если это ошибка "Анализ завершился с ошибкой" или validation_error, не продолжаем polling
      if (error instanceof Error && (
        error.message.includes('Анализ завершился с ошибкой') ||
        error.message.includes('В ситуации не обнаружен вопрос для анализа')
      )) {
        console.log('❌ Анализ завершился с ошибкой, прекращаем polling');
        throw error; // Пробрасываем ошибку дальше
      }
      
      // Ждем перед повторной попыткой только для других ошибок
      if (abortSignal) {
        await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(resolve, pollInterval);
          abortSignal.addEventListener('abort', () => {
            clearTimeout(timeoutId);
            reject(new Error('Анализ отменен пользователем'));
          });
        });
      } else {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }
  }
  
  // Если достигнуто максимальное количество попыток
  throw new Error('Превышено максимальное количество попыток проверки статуса');
}

/**
 * Получает информацию о статусе очереди
 */
export async function getQueueStatus(): Promise<any> {
  const response = await fetch(`${SHARE_SERVER_URL}/api/background/queue-status`);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ошибка: ${response.status}`);
  }

  return await response.json();
}

// Экспортируем вспомогательные функции
export { formatTime, calculateProgress, createProgressBar };
